import { useState, useEffect, useCallback, useRef } from 'react';
import { api, ChatMessageResponse, ChatAttachmentRef } from '@/lib/api';

const MESSAGE_PAGE_SIZE = 50;

/**
 * Enriching merge for two copies of the same message. The same send can land
 * twice in quick succession — once from the POST response (rich: reactions,
 * replyPreview) and once from the WebSocket echo — and either can arrive first.
 * Take each defined/non-null field from the incoming copy, otherwise keep what
 * we already have, so the fuller payload always wins regardless of order and a
 * reply never degrades to a plain message. Booleans like `isEdited` are kept
 * (only null/undefined are skipped).
 */
function mergeChatMessage(
  existing: ChatMessageResponse,
  incoming: ChatMessageResponse,
): ChatMessageResponse {
  const merged: Record<string, unknown> = { ...existing };
  for (const [key, value] of Object.entries(incoming)) {
    if (value !== undefined && value !== null) merged[key] = value;
  }
  return merged as unknown as ChatMessageResponse;
}

interface UseChatMessagesReturn {
  messages: ChatMessageResponse[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  sendMessage: (
    content: string,
    replyToId?: string,
    attachmentRefs?: ChatAttachmentRef[],
  ) => Promise<ChatMessageResponse | null>;
  editMessage: (messageId: string, content: string) => Promise<boolean>;
  deleteMessage: (messageId: string) => Promise<boolean>;
  loadMore: () => void;
  addMessage: (message: ChatMessageResponse) => void;
  updateMessage: (messageId: string, updates: Partial<ChatMessageResponse>) => void;
  applyReaction: (
    messageId: string,
    userId: string,
    emoji: string,
    action: 'add' | 'remove',
  ) => void;
  removeMessage: (messageId: string) => void;
  markAsRead: (messageId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

export function useChatMessages(channelId: string | null): UseChatMessagesReturn {
  const [messages, setMessages] = useState<ChatMessageResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const oldestMessageIdRef = useRef<string | null>(null);

  const fetchMessages = useCallback(async () => {
    if (!channelId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.listChatMessages(channelId, { limit: MESSAGE_PAGE_SIZE });
      // API returns newest-first; reverse for chronological (oldest-first) display
      setMessages([...result.messages].reverse());
      setHasMore(result.hasMore);
      oldestMessageIdRef.current = result.oldestMessageId;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [channelId]);

  const loadMore = useCallback(async () => {
    if (!channelId || loadingMore || !hasMore || !oldestMessageIdRef.current) return;
    setLoadingMore(true);
    try {
      const result = await api.listChatMessages(channelId, {
        before: oldestMessageIdRef.current,
        limit: 50,
      });
      // API returns newest-first; reverse for chronological (oldest-first) display
      const olderMessages = [...result.messages].reverse();
      setMessages((prev) => [...olderMessages, ...prev]);
      setHasMore(result.hasMore);
      oldestMessageIdRef.current = result.oldestMessageId;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more messages');
    } finally {
      setLoadingMore(false);
    }
  }, [channelId, loadingMore, hasMore]);

  const sendMessage = useCallback(
    async (
      content: string,
      replyToId?: string,
      attachmentRefs?: ChatAttachmentRef[],
    ): Promise<ChatMessageResponse | null> => {
      if (!channelId) return null;
      try {
        const result = await api.sendChatMessage(channelId, { content, replyToId, attachmentRefs });
        // Add to local message list immediately
        if (result.message) {
          setMessages((prev) => {
            // The WebSocket echo may have already added this message; merge the
            // (richer) POST response into it rather than discarding it.
            const idx = prev.findIndex((m) => m.messageId === result.message.messageId);
            if (idx === -1) return [...prev, result.message];
            const next = [...prev];
            next[idx] = mergeChatMessage(prev[idx], result.message);
            return next;
          });
        }
        return result.message;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to send message');
        return null;
      }
    },
    [channelId],
  );

  const editMessage = useCallback(async (messageId: string, content: string): Promise<boolean> => {
    try {
      const result = await api.editChatMessage(messageId, content);
      setMessages((prev) =>
        prev.map((m) => (m.messageId === messageId ? { ...m, ...result.message } : m)),
      );
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to edit message');
      return false;
    }
  }, []);

  const deleteMessage = useCallback(async (messageId: string): Promise<boolean> => {
    try {
      await api.deleteChatMessage(messageId);
      setMessages((prev) => prev.filter((m) => m.messageId !== messageId));
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete message');
      return false;
    }
  }, []);

  const addMessage = useCallback((message: ChatMessageResponse) => {
    setMessages((prev) => {
      // If this id already exists (e.g. the POST response landed first), merge
      // the incoming copy in rather than dropping it — whichever arrives second
      // fills any gaps so a reply never degrades to a plain message.
      const idx = prev.findIndex((m) => m.messageId === message.messageId);
      if (idx === -1) return [...prev, message];
      const next = [...prev];
      next[idx] = mergeChatMessage(prev[idx], message);
      return next;
    });
  }, []);

  const updateMessage = useCallback((messageId: string, updates: Partial<ChatMessageResponse>) => {
    setMessages((prev) => prev.map((m) => (m.messageId === messageId ? { ...m, ...updates } : m)));
  }, []);

  // Apply a single reaction delta from a realtime event. Idempotent on userId,
  // so a user's own optimistic reaction plus its WebSocket echo don't double
  // count, while reactions from other users now appear live (the old handler
  // re-rendered with an empty patch and only refetch surfaced them). Returns
  // the message unchanged when the delta is a no-op so memoized rows don't
  // re-render needlessly.
  const applyReaction = useCallback(
    (messageId: string, userId: string, emoji: string, action: 'add' | 'remove') => {
      setMessages((prev) =>
        prev.map((m) => {
          if (m.messageId !== messageId) return m;
          const reactions = m.reactions ?? [];
          const existing = reactions.find((r) => r.emoji === emoji);
          if (action === 'add') {
            if (existing?.userIds.includes(userId)) return m;
            const next = existing
              ? reactions.map((r) =>
                  r.emoji === emoji
                    ? { ...r, count: r.count + 1, userIds: [...r.userIds, userId] }
                    : r,
                )
              : [...reactions, { emoji, count: 1, userIds: [userId] }];
            return { ...m, reactions: next };
          }
          if (!existing?.userIds.includes(userId)) return m;
          const next = reactions
            .map((r) =>
              r.emoji === emoji
                ? { ...r, count: r.count - 1, userIds: r.userIds.filter((id) => id !== userId) }
                : r,
            )
            .filter((r) => r.count > 0);
          return { ...m, reactions: next };
        }),
      );
    },
    [],
  );

  const removeMessage = useCallback((messageId: string) => {
    setMessages((prev) => prev.filter((m) => m.messageId !== messageId));
  }, []);

  // Mark the newest message in the channel as read
  const markAsRead = useCallback(
    async (messageId: string) => {
      if (!channelId) return;
      try {
        await api.markChatChannelRead(channelId, messageId);
      } catch {
        // Non-critical — silently ignore
      }
    },
    [channelId],
  );

  useEffect(() => {
    if (channelId) {
      fetchMessages();
    } else {
      setMessages([]);
      setHasMore(false);
      oldestMessageIdRef.current = null;
    }
  }, [channelId, fetchMessages]);

  return {
    messages,
    loading,
    loadingMore,
    error,
    hasMore,
    sendMessage,
    editMessage,
    deleteMessage,
    loadMore,
    addMessage,
    updateMessage,
    applyReaction,
    removeMessage,
    markAsRead,
    refresh: fetchMessages,
  };
}
